import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetAllInvestmentsQuery } from '../store/slices/apiSlice';
import { useGetAdminDashboardDetailsQuery, useGetAllProjectsQuery, useDeleteProjectMutation, Project } from '../store/slices/apiSlice';
import { RootState } from '../store';
import { TrendingUp, Building2, DollarSign, Target, Users, TrendingDown, Edit, Trash2, Plus } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Table } from '../components/ui/Table';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: dashboardData, isLoading: dashboardLoading } = useGetAdminDashboardDetailsQuery();
  const { data: projectsResponse, isLoading: projectsLoading } = useGetAllProjectsQuery({
    page: currentPage,
    limit: itemsPerPage,
  });
  const [deleteProject, { isLoading: isDeletingProject }] = useDeleteProjectMutation();
  const projects = projectsResponse?.data || [];

  const { user } = useSelector((state: RootState) => state.auth);
  const { data: investmentsResponse } = useGetAllInvestmentsQuery();
  const investments = investmentsResponse?.data || [];
  const navigate = useNavigate();

  const totalInvested = investments
    .filter((inv: any) => inv.status === 'approved')
    .reduce((sum: number, inv: any) => sum + inv.amount, 0);
  const pendingInvestments = investments.filter((inv: any) => inv.status === 'pending').length;

  const formatCurrency = (amount: number, currencySymbol: string = '$') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount).replace('$', currencySymbol);
  };

  const handleEditProject = (project: Project) => {
    navigate('/admin/update-project', { state: { project } });
  };

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedProjectTitle, setSelectedProjectTitle] = useState('');

  const handleDeleteProject = async (projectId: string) => {
    const project = projects.find(p => p.project_id.toString() === projectId);
    setSelectedProjectId(projectId);
    setSelectedProjectTitle(project?.title || '');
    setIsDeleteConfirmationOpen(true);
  };

  const confirmDeleteProject = async () => {
    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      setIsDeleteConfirmationOpen(false);
      return;
    }

    console.log('Deleting project with token:', token.substring(0, 20) + '...');

    try {
      const result = await deleteProject({ project_id: parseInt(selectedProjectId) }).unwrap();

      if (result.success) {
        toast.success('Project deleted successfully!');
        // The projects list will automatically refresh due to cache invalidation
      } else {
        toast.error(`Failed to delete project: ${result.message}`);
      }
    } catch (error: any) {
      console.error('Error deleting project:', error);

      // Handle specific error cases
      if (error?.status === 400) {
        toast.error('Invalid request. Please check the project ID and try again.');
      } else if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 404) {
        toast.error('Project not found. It may have already been deleted.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error(error?.data?.message || 'An error occurred while deleting the project. Please try again.');
      }
    }
    setIsDeleteConfirmationOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'funded': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  // row.images && row.images.length > 0 ? row.images[0].image_url : 
  const tableColumns = [
    {
      key: 'sno',
      label: 'S.No',
      render: (_: any, __: any, index: number) => (
        <div className="font-bold text-gray-400 text-xs">#{index}</div>
      ),
    },
    {
      key: 'image',
      label: 'Image',
      render: (value: any, row: Project) => (
        row.images && row.images.length > 0 ? (
          <img
            src={row.images[0].image_url}
            alt={row.title}
            className="w-16 h-12 sm:w-20 sm:h-14 min-w-[64px] min-h-[48px] sm:min-w-[80px] sm:min-h-[56px] object-cover rounded"
            style={{ display: 'block' }}
          />
        ) : (
          <div className="w-16 h-12 sm:w-20 sm:h-14 min-w-[64px] min-h-[48px] sm:min-w-[80px] sm:min-h-[56px] rounded bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-500">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
        )
      ),
    },
    {
      key: 'title',
      label: 'Project Title',
      sortable: true,
      render: (value: string, row: Project) => (
        <div className="font-medium text-gray-900 text-sm sm:text-base">{value}</div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (value: any, row: Project) => (
        <span className="capitalize text-sm sm:text-base">{row.category?.name || 'Unknown'}</span>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
    },
    {
      key: 'funding_goal',
      label: 'Target Amount',
      sortable: true,
      render: (value: number, row: Project) => formatCurrency(value, row.currency_symbol),
    },
    {
      key: 'amount_raised',
      label: 'Raised Amount',
      sortable: true,
      render: (value: number | null, row: Project) => {
        const raised = value || 0;
        const percentage = row.funding_goal > 0 ? (raised / row.funding_goal) * 100 : 0;
        return (
          <div>
            <div className="font-medium text-sm sm:text-base">{formatCurrency(raised, row.currency_symbol)}</div>
            <div className="text-xs sm:text-sm text-gray-500">{percentage.toFixed(1)}%</div>
          </div>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value: string) => (
        <span className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${getStatusColor(value)}`}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      ),
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortable: true,
      render: (value: string) => (
        <span className="text-sm sm:text-base">{formatDateUtil(value)}</span>
      ),
    },

  ];


  if (user?.role === 'admin' || user?.role === 'manager') {

    tableColumns.push(
      {
        key: 'actions',
        label: 'Actions',
        render: (value: any, row: Project) => (
          <div className="flex space-x-1 sm:space-x-2">
            <Button
              onClick={() => handleEditProject(row)}
              variant="outline"
              size="sm"
              icon={Edit}
              disabled={isDeletingProject}
            >
              {''}
            </Button>
            <Button
              onClick={() => handleDeleteProject(row.project_id.toString())}
              variant="outline"
              size="sm"
              icon={Trash2}
              disabled={isDeletingProject}
            >
              {''}
            </Button>
          </div>
        ),
      },
    )
  }



  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    trend?: number;
    color: string;
  }> = ({ title, value, icon, trend, color }) => (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm font-medium text-gray-500">{title}</p>
          <p className="text-lg sm:text-xl lg:text-2xl font-bold">{value}</p>
          {trend !== undefined && (
            <p className={`text-xs sm:text-sm flex items-center mt-1 ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {trend >= 0 ? <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> : <TrendingDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />}
              {Math.abs(trend)}% from last month
            </p>
          )}
        </div>
        <div className={`p-2 sm:p-3 rounded-full ${color}`}>
          {icon}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6">

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {dashboardLoading ? (
          // Loading skeleton
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))
        ) : (
          <>
            {/* Total Users */}
            <StatCard
              title="Total Users"
              value={dashboardData?.data?.total_users || 0}
              icon={<Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
              color="bg-blue-500"
            />

            {/* Total Projects */}
            <StatCard
              title="Total Projects"
              value={dashboardData?.data?.total_projects || 0}
              icon={<Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
              color="bg-green-500"
            />

            {/* Total Investments */}
            <StatCard
              title="Total Investments"
              value={dashboardData?.data?.total_investments || 0}
              icon={<Target className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
              color="bg-purple-500"
            />

          </>
        )}
      </div>

      {/* Projects Table */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Recent Projects</h2>

          <Button variant="outline" size="sm" className='bg-primary-600 hover:bg-primary-700 text-white' onClick={() => navigate('/admin/projects')}> View All Projects</Button>
        </div>

        {projectsLoading ? (
          // Loading skeleton for table
          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Table
            columns={tableColumns}
            data={projects}
            searchable={false}
            onRowClick={undefined}
          />
        )}

        {!projectsLoading && projects.length === 0 && (
          <div className="text-center py-8 sm:py-12">
            <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No projects available</h3>
            <p className="text-sm sm:text-base text-gray-500">There are currently no projects to display.</p>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
        title="Delete Project"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the project{' '}
            <span className="font-semibold">{selectedProjectTitle}</span>?
            This action cannot be undone.
          </p>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmationOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteProject}
              disabled={isDeletingProject}
            >
              {isDeletingProject ? 'Deleting...' : 'Delete Project'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;